// Apolo IDE — CodeMirror 6 editor wrapper.
//
// Lazy-loads the language extension based on file extension. Auto-save is
// debounced 1s. Read-only flag drives the EditorView editable extension.
//
// Sprint v11 E1 — Cmd+K inline edit: when the user presses Cmd+K (or Ctrl+K
// on non-mac) with a non-empty selection, we capture the selection range +
// surrounding context and open <InlineEditPopup>. Accept dispatches a
// CodeMirror replaceSelection; Reject discards the proposed edit.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorState, Prec } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import { THEMES, useTheme } from '../../hooks/useTheme.js';
import InlineEditPopup from './InlineEditPopup.jsx';
import { inlineCompletionExtension } from '../../lib/inlineCompletion.js';

const CONTEXT_CHARS_PER_SIDE = 2_000;

function pickLanguage(filename) {
  if (!filename) return { extensions: [], language: 'plaintext' };
  const lower = filename.toLowerCase();
  if (lower.endsWith('.js') || lower.endsWith('.mjs') || lower.endsWith('.cjs'))
    return { extensions: [javascript({ jsx: false })], language: 'javascript' };
  if (lower.endsWith('.jsx'))
    return { extensions: [javascript({ jsx: true })], language: 'jsx' };
  if (lower.endsWith('.ts'))
    return { extensions: [javascript({ jsx: false, typescript: true })], language: 'typescript' };
  if (lower.endsWith('.tsx'))
    return { extensions: [javascript({ jsx: true, typescript: true })], language: 'tsx' };
  if (lower.endsWith('.html') || lower.endsWith('.htm'))
    return { extensions: [html()], language: 'html' };
  if (lower.endsWith('.css'))
    return { extensions: [css()], language: 'css' };
  if (lower.endsWith('.json'))
    return { extensions: [json()], language: 'json' };
  if (lower.endsWith('.md') || lower.endsWith('.markdown'))
    return { extensions: [markdown()], language: 'markdown' };
  if (lower.endsWith('.py'))
    return { extensions: [python()], language: 'python' };
  return { extensions: [], language: 'plaintext' };
}

const COMPLETION_LS_KEY = 'apolo:editor:inline-completion';

function readCompletionEnabled() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(COMPLETION_LS_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function Editor({
  filename,
  filePath,
  value,
  readOnly = false,
  onChange,
  onSave,
  sessionId,
  modelId,
}) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const { resolved } = useTheme();
  const langInfo = useMemo(() => pickLanguage(filename), [filename]);
  const langExtensions = langInfo.extensions;
  const [dirty, setDirty] = useState(false);

  // Inline edit popup state — null when closed, payload while open.
  const [pendingEdit, setPendingEdit] = useState(null);

  // Build the inline-edit Cmd+K command. Closes over setPendingEdit so the
  // popup renders on the host viewport at the selection's bottom-left.
  const openInlineEditCommand = useCallback(
    (view) => {
      const sel = view.state.selection.main;
      if (sel.empty) return false;
      const selectionText = view.state.doc.sliceString(sel.from, sel.to);
      if (!selectionText.trim()) return false;
      const docLen = view.state.doc.length;
      const beforeFrom = Math.max(0, sel.from - CONTEXT_CHARS_PER_SIDE);
      const afterTo = Math.min(docLen, sel.to + CONTEXT_CHARS_PER_SIDE);
      const contextBefore = view.state.doc.sliceString(beforeFrom, sel.from);
      const contextAfter = view.state.doc.sliceString(sel.to, afterTo);

      // Use the selection-end coordinates as the popup anchor — popup
      // appears just below the highlighted text.
      const coords = view.coordsAtPos(sel.from);
      const anchor = coords
        ? { x: coords.left, y: coords.bottom }
        : { x: 100, y: 100 };

      setPendingEdit({
        from: sel.from,
        to: sel.to,
        anchor,
        selectionText,
        contextBefore,
        contextAfter,
      });
      return true;
    },
    [],
  );

  const handleAcceptEdit = useCallback(
    (newText) => {
      const view = viewRef.current;
      if (!view || !pendingEdit) {
        setPendingEdit(null);
        return;
      }
      view.dispatch({
        changes: { from: pendingEdit.from, to: pendingEdit.to, insert: newText },
        selection: { anchor: pendingEdit.from + newText.length },
      });
      view.focus();
      setPendingEdit(null);
    },
    [pendingEdit],
  );

  const handleRejectEdit = useCallback(() => {
    setPendingEdit(null);
    viewRef.current?.focus();
  }, []);

  // Build / rebuild the editor whenever language, readOnly, or theme changes.
  // Theme change forces a teardown because CodeMirror themes attach as
  // extensions at construction time -- no runtime swap API.
  useEffect(() => {
    if (!hostRef.current) return undefined;

    const updateListener = EditorView.updateListener.of((upd) => {
      if (upd.docChanged) {
        const newText = upd.state.doc.toString();
        setDirty(true);
        onChangeRef.current?.(newText);
      }
    });

    const themeExtension = resolved === THEMES.DARK ? [oneDark] : [];

    // Cmd+K (Mod-k) wrapped with Prec.highest so it runs before any
    // language-level keybindings that might consume the chord.
    const inlineEditKeymap = Prec.highest(
      keymap.of([
        {
          key: 'Mod-k',
          preventDefault: true,
          run: openInlineEditCommand,
        },
      ]),
    );

    // Sprint v11 E5 — Tab autocomplete ghost text. The extension is added
    // BEFORE the default keymap so its Tab/Escape bindings win.
    const completionExt = inlineCompletionExtension({
      enabled: () => readOnly ? false : readCompletionEnabled(),
      getOptions: () => ({
        modelId,
        language: langInfo.language,
        filePath: filePath || filename,
      }),
    });

    const state = EditorState.create({
      doc: value ?? '',
      extensions: [
        inlineEditKeymap,
        ...completionExt,
        lineNumbers(),
        highlightActiveLine(),
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        EditorState.readOnly.of(readOnly),
        ...themeExtension,
        ...langExtensions,
        updateListener,
      ],
    });

    const view = new EditorView({
      state,
      parent: hostRef.current,
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // we intentionally exclude `value` from deps; the next useEffect syncs
    // value updates without rebuilding the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename, filePath, readOnly, langExtensions, langInfo.language, modelId, resolved, openInlineEditCommand]);

  // Sync external value updates without rebuilding the view.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({
      changes: { from: 0, to: current.length, insert: value ?? '' },
    });
    setDirty(false);
  }, [value]);

  // Debounced auto-save when content is dirty.
  useEffect(() => {
    if (!dirty) return undefined;
    const handle = setTimeout(() => {
      const view = viewRef.current;
      if (!view) return;
      onSaveRef.current?.(view.state.doc.toString());
      setDirty(false);
    }, 1000);
    return () => clearTimeout(handle);
  }, [dirty]);

  return (
    <>
      <div
        ref={hostRef}
        role="textbox"
        aria-label={filename ? `Editor: ${filename}` : 'Editor'}
        aria-readonly={readOnly}
        className="h-full w-full overflow-hidden rounded-md border border-apolo-borderSubtle text-sm"
        style={{ backgroundColor: 'var(--apolo-code-bg)' }}
      />
      {pendingEdit && (
        <InlineEditPopup
          anchor={pendingEdit.anchor}
          selectionText={pendingEdit.selectionText}
          contextBefore={pendingEdit.contextBefore}
          contextAfter={pendingEdit.contextAfter}
          filePath={filePath || filename || ''}
          language={langInfo.language}
          sessionId={sessionId}
          modelId={modelId}
          onAccept={handleAcceptEdit}
          onReject={handleRejectEdit}
        />
      )}
    </>
  );
}
